import React, { useCallback, useEffect, useRef, useState } from "react";
import { Virtuoso, type VirtuosoHandle } from "react-virtuoso";
import type { TaktColorEntry } from "../../../models";
import { TRADE_UNASSIGNED_COLOR } from "../../../models";
import type { ColorByMode } from "../filtering/attributes";
import type { MonthSpan, FlatRow, InspectorInfo } from "../types";
import type { ZoomConfig } from "../constants";
import { LEGEND_VISIBLE, clampPx } from "../constants";
import { chooseBarLabel, chooseTradeLabel, sanitizeWorkCode } from "../utils";
import LegendContent from "./LegendContent/LegendContent";
import "./TaktGrid.css";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatDateHeader(iso: string, short = false): string {
  const d = new Date(iso + "T00:00:00");
  return short ? String(d.getDate()) : `${d.getMonth() + 1}/${d.getDate()}`;
}

/* A short trailing spacer so the last (often tiny) row clears the scroll
 * container's bottom edge — the rounded border + any horizontal scrollbar would
 * otherwise clip it. Stable module-level identity keeps Virtuoso from churning. */
const ListFooter: React.FC = () => <div className="takt-list-footer" aria-hidden />;
const VIRTUOSO_COMPONENTS = { Footer: ListFooter };

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface BarClickInfo {
  workCode: string;
  label: string;
  activityIds: number[];
  start: string;
  finish: string;
  status: string;
  sectionName: string;
  taktB: string;
  taktC: string;
  isCritical: boolean;
}

export interface SectionGraphInfo {
  name: string;
  taktA: string;
  sortA: number;
}

interface Props {
  workdays: string[];
  colors: Record<string, TaktColorEntry>;
  monthSpans: MonthSpan[];
  todayIdx: number | null;
  flatRows: FlatRow[];
  colorStyleSheet: string;
  scrollerRef: React.RefObject<HTMLElement | null>;
  virtuosoRef: React.RefObject<VirtuosoHandle | null>;
  onBarClick?: (info: BarClickInfo) => void;
  /** Hover preview — feeds the bottom inspector. Click stays onBarClick. */
  onBarHover?: (info: InspectorInfo | null) => void;
  onSectionGraphClick?: (section: SectionGraphInfo) => void;
  onSectionPullPlanClick?: (section: SectionGraphInfo) => void;
  onSectionDepEditorClick?: (section: SectionGraphInfo) => void;
  cascadedActivityIds?: Set<number>;
  sourceActivityId?: number | null;
  colorBy: ColorByMode;
  /** When the critical-path filter is on, show the spotlight even if not coloring by critical. */
  criticalFilterActive?: boolean;
  zoomConfig: ZoomConfig;
  /** Raw slider px/day + the zoom commit — for ⌘/Ctrl-wheel zoom on the board. */
  pxPerDay: number;
  onZoom: (n: number, anchorX?: number) => void;
  onVisibleRangeChange?: (startIndex: number) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const TaktGrid: React.FC<Props> = ({
  workdays,
  colors,
  monthSpans,
  todayIdx,
  flatRows,
  colorStyleSheet,
  scrollerRef,
  virtuosoRef,
  onBarClick,
  onBarHover,
  onSectionGraphClick,
  onSectionPullPlanClick,
  onSectionDepEditorClick,
  cascadedActivityIds,
  sourceActivityId,
  colorBy,
  criticalFilterActive,
  zoomConfig,
  pxPerDay,
  onZoom,
  onVisibleRangeChange,
}) => {
  // ----- customScrollParent ref pattern -----
  const [mainAreaEl, setMainAreaEl] = useState<HTMLElement | null>(null);
  // The section that owns the top of the viewport, shown as a pinned banner once
  // its real header has scrolled out of view.
  const [stickySection, setStickySection] = useState<string | null>(null);
  const mainAreaRef = useCallback(
    (el: HTMLElement | null) => {
      setMainAreaEl(el);
      (scrollerRef as React.MutableRefObject<HTMLElement | null>).current = el;
    },
    [scrollerRef]
  );

  // ----- Stable refs for itemContent -----
  const {
    colWidth: CW,
    zoneColWidth: ZCW,
    legendColWidth: LCW,
    dataRowInnerH: DRIH,
    monthHeaderH,
    dateHeaderH,
    headerHeight,
    dateLabelStep,
    shortDates,
    compactHeaders,
  } = zoomConfig;
  const totalWidth = ZCW + workdays.length * CW;
  const stickyTop = headerHeight; // banner sits just below the sticky date header
  const totalWidthRef = useRef(totalWidth);
  totalWidthRef.current = totalWidth;
  const todayIdxRef = useRef(todayIdx);
  todayIdxRef.current = todayIdx;

  const zoomRef = useRef(zoomConfig);
  zoomRef.current = zoomConfig;

  const flatRowsRef = useRef(flatRows);
  flatRowsRef.current = flatRows;

  const colorsRef = useRef(colors);
  colorsRef.current = colors;


  const onBarClickRef = useRef(onBarClick);
  onBarClickRef.current = onBarClick;

  const onSectionGraphClickRef = useRef(onSectionGraphClick);
  onSectionGraphClickRef.current = onSectionGraphClick;

  const onSectionPullPlanClickRef = useRef(onSectionPullPlanClick);
  onSectionPullPlanClickRef.current = onSectionPullPlanClick;

  const onSectionDepEditorClickRef = useRef(onSectionDepEditorClick);
  onSectionDepEditorClickRef.current = onSectionDepEditorClick;

  const cascadedIdsRef = useRef(cascadedActivityIds);
  cascadedIdsRef.current = cascadedActivityIds;
  const sourceIdRef = useRef(sourceActivityId);
  sourceIdRef.current = sourceActivityId;
  const cpMode = colorBy === "critical" || !!criticalFilterActive;
  const colorNone = colorBy === "none";
  const cpModeRef = useRef(cpMode);
  cpModeRef.current = cpMode;

  // Re-center the viewport on today (the today flag in the header is the click
  // target) — the one-tap way back to "now" after panning.
  const scrollToToday = useCallback(() => {
    const el = mainAreaEl;
    if (!el || todayIdx == null) return;
    const z = zoomRef.current;
    // Center today in the day area, not the full viewport — the zone-label
    // column is sticky on the left and occludes its width.
    const x =
      z.zoneColWidth + todayIdx * z.colWidth + z.colWidth / 2 - (el.clientWidth + z.zoneColWidth) / 2;
    el.scrollTo({ left: Math.max(0, x), behavior: "smooth" });
  }, [mainAreaEl, todayIdx]);

  // ----- Grab-to-pan + ⌘/Ctrl-wheel zoom -----
  // Drag anywhere on the board to pan both axes; a drag past a small threshold
  // suppresses the trailing bar click. ⌘/Ctrl + wheel zooms into the cursor.
  const pxPerDayRef = useRef(pxPerDay);
  pxPerDayRef.current = pxPerDay;
  const onZoomRef = useRef(onZoom);
  onZoomRef.current = onZoom;
  const dragRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const didDragRef = useRef(false);
  const handlePanDown = useCallback(
    (e: React.MouseEvent) => {
      const el = mainAreaEl;
      if (e.button !== 0 || !el) return;
      // Leave real controls (buttons, inputs, the today flag) to their handlers.
      if ((e.target as HTMLElement).closest?.("button, input, a, .takt-today-flag")) return;
      dragRef.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
      didDragRef.current = false;
    },
    [mainAreaEl],
  );
  useEffect(() => {
    const el = mainAreaEl;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      if (!didDragRef.current && Math.abs(dx) + Math.abs(dy) > 4) {
        didDragRef.current = true;
        el.classList.add("takt-panning");
      }
      if (didDragRef.current) {
        el.scrollLeft = d.left - dx;
        el.scrollTop = d.top - dy;
      }
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      el.classList.remove("takt-panning");
      // Hold the drag flag through the trailing click so a pan never opens a bar.
      if (didDragRef.current) setTimeout(() => (didDragRef.current = false), 0);
    };
    const onWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
      const anchorX = e.clientX - el.getBoundingClientRect().left;
      onZoomRef.current(clampPx(pxPerDayRef.current * factor), anchorX);
      // Bars re-lay-out under a stationary cursor, so the last-read bar is now
      // stale — drop it; the next mouse move refills the inspector.
      onBarHoverRef.current?.(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      el.removeEventListener("wheel", onWheel);
    };
  }, [mainAreaEl]);

  // ----- Hover bus (zero grid re-renders): read the hovered bar's data-*
  //       attributes + live color map, feed only the sibling inspector. -----
  const onBarHoverRef = useRef(onBarHover);
  onBarHoverRef.current = onBarHover;
  const readBar = (target: HTMLElement | null): InspectorInfo | null => {
    const bar = target?.closest?.(".takt-bar") as HTMLElement | null;
    if (!bar) return null;
    const wc = bar.getAttribute("data-wc") || "";
    const c = colorsRef.current[wc];
    const f = bar.getAttribute("data-float");
    const rd = bar.getAttribute("data-rem-dur");
    return {
      fill: c?.fill || "#3a3d3a",
      font: c?.font || "#e8e8e6",
      workCode: wc,
      label: bar.getAttribute("data-label") || "",
      taskName: bar.getAttribute("data-task-name") || "",
      start: bar.getAttribute("data-start") || "",
      finish: bar.getAttribute("data-finish") || "",
      remDur: rd == null || rd === "" ? null : Number(rd),
      floatDays: f == null || f === "" ? null : Number(f),
      status: bar.getAttribute("data-status") || "",
      trade: bar.getAttribute("data-trade") || "",
      tradeFill: bar.getAttribute("data-trade-fill") || "",
    };
  };
  const handleBarOver = useCallback((e: React.MouseEvent) => {
    const cb = onBarHoverRef.current;
    if (!cb) return;
    const info = readBar(e.target as HTMLElement);
    if (info) cb(info);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleBarOut = useCallback((e: React.MouseEvent) => {
    const cb = onBarHoverRef.current;
    if (!cb) return;
    // Don't clear when sliding bar→bar (the next mouseover refills); only clear
    // when leaving to non-bar space.
    const to = e.relatedTarget as HTMLElement | null;
    if (!to || !to.closest || !to.closest(".takt-bar")) cb(null);
  }, []);

  const itemContent = useCallback(
    (index: number) => {
      const row = flatRowsRef.current[index];
      if (!row) return null;
      const tw = totalWidthRef.current;
      const ti = todayIdxRef.current;
      const z = zoomRef.current;
      const cw = z.colWidth;
      const zcw = z.zoneColWidth;
      const lcw = z.legendColWidth;
      const drih = z.dataRowInnerH;
      const zhh = z.zoneHeaderTotalH;
      const srh = z.sectionRowTotalH;
      const ticks = z.showDayTicks;

      switch (row.type) {
        case "section-header":
          return (
            <div className="takt-section-row" style={{ width: tw + lcw, minHeight: srh }}>
              {LEGEND_VISIBLE && (
                <div className="takt-legend-sticky">
                  <LegendContent
                    name={row.section.name}
                    codes={row.codes}
                    colors={colorsRef.current}
                    height={row.legendHeight}
                    legendWidth={lcw}
                  />
                </div>
              )}
              <div className="takt-section-name-inner">
                <span>{row.section.name}</span>
                {onSectionDepEditorClickRef.current && (
                  <button
                    className="takt-section-deps-btn"
                    title="Edit dependencies for this section"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSectionDepEditorClickRef.current!({
                        name: row.section.name,
                        taktA: row.section.takt_a,
                        sortA: row.section.sort_a,
                      });
                    }}
                  >
                    Dependencies
                  </button>
                )}
                {onSectionPullPlanClickRef.current && (
                  <button
                    className="takt-section-pullplan-btn"
                    title="Open in Pull Plan"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSectionPullPlanClickRef.current!({
                        name: row.section.name,
                        taktA: row.section.takt_a,
                        sortA: row.section.sort_a,
                      });
                    }}
                  >
                    Plan
                  </button>
                )}
                {/* The ⇄ dependencies-graph button is hidden for now; its handler
                    wiring (onSectionGraphClick) is kept intact. */}
              </div>
              {ti !== null && (
                <div
                  className="takt-today-line"
                  style={{
                    position: "absolute",
                    left: lcw + zcw + ti * cw,
                    top: 0,
                    bottom: 0,
                    width: cw,
                  }}
                />
              )}
            </div>
          );

        case "zone-header":
          return (
            <div className="takt-zone-row" style={{ width: tw, position: "relative", height: zhh }}>
              <div className="takt-zone-label-header">{row.takt_b}</div>
              {ti !== null && (
                <div
                  className="takt-today-line"
                  style={{
                    position: "absolute",
                    left: zcw + ti * cw,
                    top: 0,
                    bottom: 0,
                    width: cw,
                  }}
                />
              )}
            </div>
          );

        case "data-row": {
          const prev = index > 0 ? flatRowsRef.current[index - 1] : null;
          // At extreme zoom-out the per-row level repeats ("LVL3 LVL3 LVL2…")
          // into visual stutter — keep the solid gutter box but print the level
          // only on the first row of each identical run.
          const hideLevel =
            z.compactHeaders &&
            prev?.type === "data-row" &&
            prev.taktC === row.taktC;
          return (
            <div
              className={`takt-data-row${row.isFirstInGroup ? " takt-group-first" : ""}`}
              style={{ width: tw, height: drih, position: "relative" }}
            >
              <div className="takt-zone-label">{hideLevel ? "" : row.taktC}</div>
              {row.bars.map((bar, i) => {
                const cls = sanitizeWorkCode(bar.workCode);
                // bars[i] corresponds 1:1 with slot[i]
                const slotEntry = row.slot[i];
                const isSource = slotEntry && sourceIdRef.current != null &&
                  slotEntry.activity_ids.includes(sourceIdRef.current);
                const isCascaded = !isSource && slotEntry && cascadedIdsRef.current &&
                  cascadedIdsRef.current.size > 0 &&
                  slotEntry.activity_ids.some((aid) => cascadedIdsRef.current?.has(aid));
                const cascadeClass = isSource
                  ? " takt-bar-cascade-source"
                  : isCascaded
                  ? " takt-bar-cascaded"
                  : "";
                const criticalClass =
                  cpModeRef.current && slotEntry?.is_critical
                    ? " takt-bar-critical-path"
                    : "";
                // Coloring by trade shows the trade name (CSS-truncated, no
                // work-code fallback); otherwise the normal label ladder.
                const isTrade = colorBy === "trade";
                const lab = isTrade
                  ? chooseTradeLabel(bar.tradeName, bar.width)
                  : chooseBarLabel(bar.label, bar.workCode, bar.width);
                // Color-by-trade resolves PER BAR (by task_code) — the work-code
                // CSS class can't express a code that's one trade in section A
                // and another in section B — so paint the fill inline from the
                // bar's own trade (grey when unassigned). Other modes keep the
                // work-code class fill.
                const tradeStyle = isTrade
                  ? {
                      backgroundColor: bar.tradeFill || TRADE_UNASSIGNED_COLOR.fill,
                      color: bar.tradeFont || TRADE_UNASSIGNED_COLOR.font,
                    }
                  : null;
                return (
                  <div
                    key={i}
                    className={`takt-bar takt-wc-${cls}${cascadeClass}${criticalClass}${ticks ? " takt-bar--ticks" : ""}`}
                    style={{
                      position: "absolute",
                      left: zcw + bar.left,
                      width: bar.width,
                      top: 0,
                      bottom: 0,
                      cursor: onBarClickRef.current ? "pointer" : undefined,
                      ...tradeStyle,
                    }}
                    data-wc={bar.workCode}
                    data-label={bar.label}
                    data-task-name={slotEntry?.task_name}
                    data-start={slotEntry?.start}
                    data-finish={slotEntry?.finish}
                    data-float={slotEntry?.float_days == null ? "" : String(slotEntry.float_days)}
                    data-status={slotEntry?.status}
                    data-rem-dur={slotEntry?.remaining_duration == null ? "" : String(slotEntry.remaining_duration)}
                    data-trade={bar.tradeName}
                    data-trade-fill={bar.tradeFill}
                    onClick={
                      onBarClickRef.current && slotEntry
                        ? (e) => {
                            if (didDragRef.current) return; // a pan, not a click
                            e.stopPropagation();
                            onBarClickRef.current!({
                              workCode: bar.workCode,
                              label: bar.label,
                              activityIds: slotEntry.activity_ids,
                              start: slotEntry.start,
                              finish: slotEntry.finish,
                              status: slotEntry.status,
                              sectionName: row.sectionName,
                              taktB: row.taktB,
                              taktC: row.taktC,
                              isCritical: slotEntry.is_critical,
                            });
                          }
                        : undefined
                    }
                  >
                    {lab && (
                      <span className="takt-bar-label" style={{ textAlign: lab.align }}>
                        {lab.code && <b className="takt-bar-code">{lab.code}</b>}
                        {lab.text}
                      </span>
                    )}
                  </div>
                );
              })}
              {ti !== null && (
                <div
                  className="takt-today-line"
                  style={{
                    position: "absolute",
                    left: zcw + ti * cw,
                    top: 0,
                    bottom: 0,
                    width: cw,
                  }}
                />
              )}
            </div>
          );
        }

        case "spacer":
          return (
            <div className="takt-spacer-row" style={{ width: tw, height: drih, position: "relative" }}>
              {ti !== null && (
                <div
                  className="takt-today-line"
                  style={{
                    position: "absolute",
                    left: zcw + ti * cw,
                    top: 0,
                    bottom: 0,
                    width: cw,
                  }}
                />
              )}
            </div>
          );
      }
    },
    // flatRows dep forces Virtuoso to re-render visible items on local edits.
    // Refs alone don't trigger re-render — Virtuoso needs a new itemContent reference.
    // zoomConfig omitted: flatRows already recomputes when zoom changes, so it
    // already triggers a new callback identity. Zoom values are read from refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatRows, cascadedActivityIds, sourceActivityId, colors, colorBy, criticalFilterActive]
  );

  if (flatRows.length === 0) return null;

  const cssVars = {
    "--takt-col-width": `${CW}px`,
    "--takt-zone-col-width": `${ZCW}px`,
    "--takt-legend-col-width": `${LCW}px`,
  } as React.CSSProperties;

  return (
    <div
      className={`takt-grid-wrapper${cpMode ? " takt-cp-mode" : ""}${colorNone ? " takt-color-none" : ""}${compactHeaders ? " takt-compact-headers" : ""}`}
      style={cssVars}
      onMouseOver={handleBarOver}
      onMouseOut={handleBarOut}
      onMouseDown={handlePanDown}
    >
      {/* Inject color CSS classes */}
      {colorStyleSheet && (
        <style dangerouslySetInnerHTML={{ __html: colorStyleSheet }} />
      )}

      {/* Main scrollable area (both axes) */}
      <div className="takt-main-area" ref={mainAreaRef}>
        {/* Sticky header */}
        <div className="takt-header" style={{ width: totalWidth }}>
          {/* Month row */}
          <div className="takt-header-month" style={{ position: "relative", height: monthHeaderH }}>
            <div className="takt-header-zone-cell" />
            {monthSpans.map((m, i) => (
              <span
                key={i}
                className="takt-month-cell"
                style={{
                  position: "absolute",
                  left: ZCW + m.startIdx * CW,
                  width: m.colSpan * CW,
                  top: 0,
                  bottom: 0,
                }}
              >
                {m.label}
              </span>
            ))}
          </div>
          {/* Date row — cadence thins out as you zoom out; step the loop so we
              only emit the kept cells (not a span-or-null per workday). */}
          <div className="takt-header-date" style={{ position: "relative", height: dateHeaderH }}>
            <div className="takt-header-zone-cell" />
            {Array.from(
              { length: Math.ceil(workdays.length / dateLabelStep) },
              (_, k) => {
                const i = k * dateLabelStep;
                return (
                  <span
                    key={i}
                    className={`takt-date-cell${i === todayIdx ? " takt-today" : ""}`}
                    style={{
                      position: "absolute",
                      left: ZCW + i * CW,
                      width: CW * dateLabelStep,
                      top: 0,
                      bottom: 0,
                    }}
                  >
                    {formatDateHeader(workdays[i], shortDates)}
                  </span>
                );
              },
            )}
          </div>
          {todayIdx !== null && (
            <div
              className="takt-today-flag"
              style={{ left: ZCW + todayIdx * CW, width: CW }}
              onClick={scrollToToday}
              title="Jump to today"
              role="button"
            >
              <span className="takt-today-flag__cap" />
            </div>
          )}
        </div>

        {/* Virtuoso body */}
        {mainAreaEl && (
          <Virtuoso
            ref={virtuosoRef}
            customScrollParent={mainAreaEl}
            totalCount={flatRows.length}
            defaultItemHeight={DRIH}
            overscan={CW < 26 ? 800 : 400}
            itemContent={itemContent}
            components={VIRTUOSO_COMPONENTS}
            rangeChanged={(r) => {
              onVisibleRangeChange?.(r.startIndex);
              // Pin the governing section's name, but only once its real header
              // row has scrolled above the top of the viewport.
              const rows = flatRowsRef.current;
              let name: string | null = null;
              let idx = -1;
              for (let i = Math.min(r.startIndex, rows.length - 1); i >= 0; i--) {
                const row = rows[i];
                if (row && row.type === "section-header") {
                  name = row.section.name;
                  idx = i;
                  break;
                }
              }
              setStickySection(idx >= 0 && idx < r.startIndex ? name : null);
            }}
          />
        )}
      </div>
      {stickySection && (
        <div className="takt-sticky-section" style={{ top: stickyTop }}>
          <span className="takt-sticky-section__name">{stickySection}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(TaktGrid);
